/**
 * step-manager.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { PartialObserver } from "rxjs/Observer";
import {
  Observable,
  Subject,
  Subscription
} from "rxjs/Rx";

import { Comment } from "../../../intake-form/comment";
import { TransformerService } from "./transformer.service";

import {
  AlgorithmStep,
  ComputationStep,
  IntakeAnswer,
  IntakeStep,
  ReportStep
} from "../../structure";
import {
  CommentChange,
  Focalization,
  GlobalDeactivation,
  ReceivedAnswer,
  StepDeactivation,
  StepDefinition,
  StepUpdate
} from "./step-update";

/**
 * The {@link StepManager} is the central interface for creating
 * {@link AlgorithmStep}s and events related to them. It holds the source of
 * truth for registered steps and issues streams of {@link StepUpdate} events.
 */
export class StepManager
{
  /**
   * Create a {@link StepManager}
   */
  public constructor (private transformer : TransformerService) {}

  /**
   * The primary record of seen steps, indexed by their ids.
   */
  private map : Map<number, AlgorithmStep> = new Map<number, AlgorithmStep>();

  /**
   * Return the {@link AlgorithmStep} with a given id.
   */
  public get (id : number) : AlgorithmStep
  {
    return this.map.get(id);
  }

  /**
   * Identify whether the manager has a step with this id.
   */
  public has (id : number) : boolean
  {
    return this.map.has(id);
  }

  /**
   * Return a list of all of the known step IDs.
   */
  public knownIds () : number[]
  {
    return Array.from(this.map.keys());
  }

  /**
   * Return all of the {@link AlgorithmStep}s this manager knows.
   */
  public all () : AlgorithmStep[]
  {
    return Array.from(this.map.values());
  }

  /**
   * Shortcut to retrieve a subset of the {@link AlgorithmStep}s that satisfy
   * a filtering predicate.
   */
  public filtered (filter : (step : AlgorithmStep) => boolean) : AlgorithmStep[]
  {
    return this.all().filter(filter);
  }

  /**
   * Shortcut to access all of the known {@link IntakeStep}s
   */
  public intakes () : IntakeStep[]
  {
    return this.filtered(
      (step : AlgorithmStep) => (step instanceof IntakeStep)
    ) as IntakeStep[];
  }

  /**
   * Shortcut to access all of the known {@link ReportStep}s
   */
  public reports () : ReportStep[]
  {
    return this.filtered(
      (step : AlgorithmStep) => (step instanceof ReportStep)
    ) as ReportStep[];
  }

  /**
   * Shortcut to access all of the known {@link ComputationStep}s
   */
  public computations () : ComputationStep[]
  {
    return this.filtered(
      (step : AlgorithmStep) => (step instanceof ComputationStep)
    ) as ComputationStep[];
  }

  /**
   * The source of truth for the updates, exposed as Subject to allow internal
   * members to emit new updates, whereas external code can only consume them.
   */
  private _updates : Subject<StepUpdate<AlgorithmStep, AlgorithmStep>> =
    new Subject<StepUpdate<AlgorithmStep, AlgorithmStep>>();
  /**
   * An Observable of all the {@link StepUpdate}s as they occur
   */
  public get updates () : Observable<StepUpdate<AlgorithmStep, AlgorithmStep>>
  {
    return this._updates;
  }

  /**
   * Shortcut to subscribe to the update stream.
   * @param  {function(val: StepUpdate): void} next
   * @param  {function(err: any): void} error
   * @param  {function(): void} complete
   * @return {Subscription}
   */
  public subscribe (
    next : ((val : StepUpdate<AlgorithmStep, AlgorithmStep>) => void)
      | PartialObserver<StepUpdate<AlgorithmStep, AlgorithmStep>>,
    error? : (err : any) => void,
    complete? : () => void
  ) : Subscription
  {
    if (error)
    {
      return this.updates.subscribe(
        next as (val : StepUpdate<AlgorithmStep, AlgorithmStep>) => void,
        error,
        complete
      );
    }
    else
    {
      return this.updates.subscribe(
        next as PartialObserver<StepUpdate<AlgorithmStep, AlgorithmStep>>);
    }
  }

  /**
   * Apply a {@link StepUpdate} to a known step (or create it if necessary and
   * allowable), then emit the update on the public updates stream.
   */
  private applyAndSend (update : StepUpdate<any, any>) : void
  {
    const found : boolean = update.getAndApply(this.map);
    // const target : AlgorithmStep = this.map.get(update.id);
    if (!found)
    {
      if (update.canCreate)
      {
        const step : AlgorithmStep = update.create();
        this.map.set(update.id, step);
      }
      else
      {
        console.error(this.map, update);
        throw new Error("Illegal update application: no local step");
      }
    }
    this._updates.next(update);
  }

  /**
   * Apply and emit a new {@link StepDefinition}.
   */
  public define (step : AlgorithmStep) : void
  {
    this.applyAndSend(new StepDefinition<any>(step, this.transformer));
  }

  /**
   * Apply and emit a new {@link IntakeAnswer}.
   */
  public answer (answer : IntakeAnswer) : void
  {
    this.applyAndSend(new ReceivedAnswer(answer, this.transformer));
  }

  /**
   * Apply and emit a new {@link CommentChange}.
   */
  public comment (id : number, comments : Comment[]) : void
  {
    this.applyAndSend(new CommentChange(id, comments));
  }

  /**
   * Apply and emit a new {@link Focalization}.
   */
  public focalize (id : number, reason : string, duration : number) : void
  {
    this.applyAndSend(new Focalization({id, reason, duration}));
  }

  /**
   * Apply and emit a new {@link GlobalDeactivation}.
   */
  public deactivateAll () : void
  {
    this.applyAndSend(new GlobalDeactivation({id: -1}));
  }

  public close () : void
  {
    // this.deactivateAll();
    this._updates.complete();
    this.map.clear();
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/pipeline/step-manager.ts